import { useTranslation } from '@msdk/common'
import {
  ClickAwayListener,
  COLORS,
  ErrorIcon,
  Paper,
  styled,
  Typography,
  uiTokens,
  WarningIcon,
  Z_INDEX,
} from '@msdk/ui'
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { stringFormat } from '../../utils'
import { ValidationMessageDisplay, ValidationMessages } from '../ValidationMessages'
import { getPanelGutterOffset, PANEL_WIDTH, STICKY_OFFSET } from './panel-placement'
import { ValidationCategory, ValidationIssue } from './validation-panel-utils'

const PANEL_HEADER_HEIGHT = 40
const PANEL_PADDING = 12
const FADE_DURATION = 200
const ERROR_HEADER_BACKGROUND = '#FFF0F0'

// Reserve sticky height for positioning, then cancel its impact on form layout.
const StickyContainer = styled('div')`
  position: sticky;
  top: ${STICKY_OFFSET}px;
  z-index: ${Z_INDEX.sticky};
  height: ${PANEL_HEADER_HEIGHT}px;
  margin-bottom: -${PANEL_HEADER_HEIGHT}px;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  overflow: visible;
  pointer-events: none;
`

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== '$category' && prop !== '$fading' && prop !== '$gutterOffset',
})<{
  $category: ValidationCategory
  $fading: boolean
  $gutterOffset: number
}>`
  width: min(${PANEL_WIDTH}px, calc(100% - ${STICKY_OFFSET}px));
  transform: translateX(${(props) => props.$gutterOffset}px);
  overflow: hidden;
  pointer-events: auto;
  border: 1px solid ${COLORS.blueGrey100};
  border-radius: ${uiTokens.radius.large}px;
  opacity: ${(props) => (props.$fading ? 0 : 1)};
  pointer-events: ${(props) => (props.$fading ? 'none' : 'auto')};
  transition: opacity ${FADE_DURATION}ms ease-out;
`

const Header = styled('div', { shouldForwardProp: (prop) => prop !== '$category' })<{
  $category: ValidationCategory
}>`
  height: ${PANEL_HEADER_HEIGHT}px;
  padding: 0 ${PANEL_PADDING}px;
  display: flex;
  align-items: center;
  gap: ${uiTokens.spacing.gap.xxSmall}px;
  color: ${(props) =>
    props.$category === ValidationCategory.Error ? COLORS.validation.error.main : COLORS.validation.warning.main};
  background-color: ${(props) =>
    props.$category === ValidationCategory.Error ? ERROR_HEADER_BACKGROUND : COLORS.validation.warning.light};

  svg {
    width: ${uiTokens.sizing.icon.xSmall}px;
    height: ${uiTokens.sizing.icon.xSmall}px;
  }
`

const Summary = styled(Typography)`
  flex: 1;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
`

const DetailsButton = styled('button')`
  min-width: auto;
  padding: 0;
  border: 0;
  background: none;
  color: ${COLORS.primary.main};
  cursor: pointer;
  font-size: 10px;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: 16px;
  text-transform: none;

  &:focus-visible {
    outline: 2px solid ${COLORS.primary.main};
    outline-offset: 2px;
  }
`

const Details = styled('div')`
  max-height: min(360px, calc(100vh - 160px));
  padding: ${PANEL_PADDING}px;
  overflow-y: auto;
  background-color: ${COLORS.white.main};
`

const SectionHeading = styled(Typography)`
  font-size: 11px;
  font-weight: ${uiTokens.font.fontWeight.semiBold};
  line-height: 16px;
`

const IssueList = styled('ol')`
  margin: ${uiTokens.spacing.padding.small}px 0 ${uiTokens.spacing.padding.medium}px;
  padding-left: ${PANEL_PADDING}px;

  &:last-child {
    margin-bottom: 0;
  }
`

const IssueItem = styled('li')`
  margin-bottom: ${uiTokens.spacing.padding.small}px;
  color: ${COLORS.text.secondary};
  font-size: 11px;
  line-height: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  .MuiFormHelperText-root {
    display: block;
    margin: 2px 0 0;
    padding: 0;
    color: ${COLORS.text.secondary};
    font-size: 11px;
    line-height: 16px;
  }

  .FormHelperText-stateIcon {
    display: none;
  }
`

const FieldLink = styled('button')`
  display: block;
  padding: 0;
  min-width: 0;
  border: 0;
  background: none;
  color: ${COLORS.primary.main};
  cursor: pointer;
  font-size: 11px;
  line-height: 16px;
  text-align: left;
  text-decoration: underline;
  text-transform: none;

  &:focus-visible {
    outline: 2px solid ${COLORS.primary.main};
    outline-offset: 2px;
  }
`

interface Props {
  issues: ValidationIssue[]
  onJump: (issue: ValidationIssue) => void
}

function getSummary(errorCount: number, warningCount: number, t: ReturnType<typeof useTranslation>): string {
  if (errorCount && warningCount) {
    const errorSummary =
      errorCount === 1
        ? stringFormat(t('CMPSR_VALIDATION_ERROR_COUNT_SINGLE', '{0} issue'), errorCount.toString())
        : stringFormat(t('CMPSR_VALIDATION_ERROR_COUNT', '{0} issues'), errorCount.toString())
    const warningSummary =
      warningCount === 1
        ? stringFormat(t('CMPSR_VALIDATION_WARNING_COUNT_SINGLE', '{0} warning'), warningCount.toString())
        : stringFormat(t('CMPSR_VALIDATION_WARNING_COUNT', '{0} warnings'), warningCount.toString())

    return stringFormat(t('CMPSR_VALIDATION_MIXED_SUMMARY', '{0}, {1}'), errorSummary, warningSummary)
  }

  if (errorCount) {
    const template =
      errorCount === 1
        ? t('CMPSR_VALIDATION_ERROR_SUMMARY_SINGLE', '{0} issue to review')
        : t('CMPSR_VALIDATION_ERROR_SUMMARY', '{0} issues to review')
    return stringFormat(template, errorCount.toString())
  }

  const template =
    warningCount === 1
      ? t('CMPSR_VALIDATION_WARNING_SUMMARY_SINGLE', '{0} warning to review')
      : t('CMPSR_VALIDATION_WARNING_SUMMARY', '{0} warnings to review')
  return stringFormat(template, warningCount.toString())
}

export function ValidationPanel({ issues, onJump }: Props) {
  const t = useTranslation()
  const detailsId = useId()
  const detailsButtonRef = useRef<HTMLButtonElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [gutterOffset, setGutterOffset] = useState(0)
  const [previousIssues, setPreviousIssues] = useState<ValidationIssue[]>([])
  const [fading, setFading] = useState(false)

  const visibleIssues = issues.length ? issues : previousIssues
  const errors = useMemo(
    () => visibleIssues.filter((issue) => issue.category === ValidationCategory.Error),
    [visibleIssues],
  )
  const warnings = useMemo(
    () => visibleIssues.filter((issue) => issue.category === ValidationCategory.Warning),
    [visibleIssues],
  )
  const category = errors.length ? ValidationCategory.Error : ValidationCategory.Warning

  useLayoutEffect(() => {
    const form = stickyRef.current?.parentElement
    if (!form) {
      return
    }

    // Use the nearest scrollport so an outside panel is never clipped by a modal.
    let boundary = form.parentElement
    while (boundary) {
      const style = window.getComputedStyle(boundary)
      if (/(auto|scroll|hidden|clip)/.test(`${style.overflowX} ${style.overflowY}`)) {
        break
      }
      boundary = boundary.parentElement
    }

    const update = () => {
      // clientWidth excludes the scrollbar from the usable gutter.
      const boundaryRight = boundary ? boundary.getBoundingClientRect().left + boundary.clientWidth : window.innerWidth
      const rightEdge = Math.min(boundaryRight, window.innerWidth)
      setGutterOffset(getPanelGutterOffset(form.getBoundingClientRect().right, rightEdge))
    }

    update()
    window.addEventListener('resize', update)

    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update)
    observer?.observe(form)
    if (boundary) {
      observer?.observe(boundary)
    }

    return () => {
      window.removeEventListener('resize', update)
      observer?.disconnect()
    }
  }, [visibleIssues.length])

  useEffect(() => {
    if (issues.length) {
      setPreviousIssues((current) => {
        const currentIds = current.map((issue) => issue.id).join()
        const nextIds = issues.map((issue) => issue.id).join()
        return currentIds === nextIds ? current : issues
      })
      setFading(false)
      return
    }

    if (previousIssues.length) {
      setExpanded(false)
      setFading(true)
    }
  }, [issues, previousIssues.length])

  useEffect(() => {
    if (!fading) {
      return
    }

    const timeout = window.setTimeout(() => {
      setPreviousIssues([])
      setFading(false)
    }, FADE_DURATION)

    return () => window.clearTimeout(timeout)
  }, [fading])

  useEffect(() => {
    if (!expanded) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false)
        detailsButtonRef.current?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [expanded])

  if (!visibleIssues.length) {
    return null
  }

  const summary = getSummary(errors.length, warnings.length, t)

  const renderIssues = (items: ValidationIssue[]) => (
    <IssueList>
      {items.map((issue) => {
        const fieldLabel = t(issue.fieldDef.idml, issue.fieldDef.description)
        const linkLabel = issue.rowNumber
          ? stringFormat(t('CMPSR_VALIDATION_FIELD_ROW_LINK', '{0}, row {1}'), fieldLabel, issue.rowNumber.toString())
          : fieldLabel

        return (
          <IssueItem key={issue.id}>
            <FieldLink
              type="button"
              onClick={() => {
                setExpanded(false)
                onJump(issue)
              }}
            >
              {linkLabel}
            </FieldLink>
            <ValidationMessages
              messages={[issue.code]}
              fieldDef={issue.fieldDef}
              node={issue.node}
              display={ValidationMessageDisplay.Summary}
            />
          </IssueItem>
        )
      })}
    </IssueList>
  )

  return (
    <StickyContainer ref={stickyRef}>
      <ClickAwayListener onClickAway={() => expanded && setExpanded(false)}>
        <StyledPaper
          elevation={2}
          $category={category}
          $fading={fading}
          $gutterOffset={gutterOffset}
          onTransitionEnd={() => {
            if (fading) {
              setPreviousIssues([])
              setFading(false)
            }
          }}
        >
          <Header $category={category}>
            {category === ValidationCategory.Error ? (
              <ErrorIcon fontSize="small" aria-hidden />
            ) : (
              <WarningIcon fontSize="small" aria-hidden />
            )}
            <Summary variant="body1" aria-live="polite">
              {summary}
            </Summary>
            <DetailsButton
              ref={detailsButtonRef}
              type="button"
              aria-expanded={expanded}
              aria-controls={detailsId}
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded
                ? t('CMPSR_VALIDATION_HIDE_DETAILS', 'Hide details')
                : t('CMPSR_VALIDATION_SHOW_DETAILS', 'Show details')}
            </DetailsButton>
          </Header>
          {expanded && (
            <Details id={detailsId}>
              {!!errors.length && (
                <section>
                  <SectionHeading variant="subtitle2">
                    {stringFormat(t('CMPSR_VALIDATION_ISSUES_HEADING', 'Issues ({0})'), errors.length.toString())}
                  </SectionHeading>
                  {renderIssues(errors)}
                </section>
              )}
              {!!warnings.length && (
                <section>
                  <SectionHeading variant="subtitle2">
                    {stringFormat(t('CMPSR_VALIDATION_WARNINGS_HEADING', 'Warnings ({0})'), warnings.length.toString())}
                  </SectionHeading>
                  {renderIssues(warnings)}
                </section>
              )}
            </Details>
          )}
        </StyledPaper>
      </ClickAwayListener>
    </StickyContainer>
  )
}
